import type { DbExecutor } from "../executor";
import { generateId } from "../ids";
import {
  validateProposedCostLineInput,
  type CostType,
  type ProposedCostLine,
  type ProposedCostLineInput,
} from "../../domain/proposedCostLine";
import { ValidationError } from "./projectRepository";

interface ProposedRow {
  id: string;
  project_id: string;
  description: string;
  amount_cents: number;
  category_id: string | null;
  cost_type: string | null;
  feature_id: string | null;
  assembly_component_id: string | null;
  calibration_rate_id: string | null;
  source: string;
  sort_order: number;
  notes: string;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

function mapLine(row: ProposedRow): ProposedCostLine {
  return {
    id: row.id,
    projectId: row.project_id,
    description: row.description,
    amountCents: Number(row.amount_cents),
    categoryId: row.category_id,
    costType: row.cost_type as CostType | null,
    featureId: row.feature_id,
    assemblyComponentId: row.assembly_component_id,
    calibrationRateId: row.calibration_rate_id ?? null,
    source: row.source as "manual" | "assembly",
    sortOrder: Number(row.sort_order),
    notes: row.notes,
    archivedAt: row.archived_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

// SELECT * so the ERA-preservation fixtures can seed through this
// repository against genuinely old schemas: a column a later migration
// added (calibration_rate_id, v39) simply reads as undefined there and
// maps to null. The mapper names every column it uses.
const SELECT = `SELECT * FROM proposed_cost_lines`;

export class ProposedCostRepository {
  constructor(private readonly db: DbExecutor) {}

  /** A linked feature must exist and belong to the same project. */
  private async assertFeatureValid(projectId: string, featureId: string | null): Promise<void> {
    if (featureId == null) return;
    const rows = await this.db.select<{ project_id: string }>(
      "SELECT project_id FROM project_features WHERE id = ?",
      [featureId],
    );
    if (rows.length === 0) {
      throw new ValidationError(["The linked feature no longer exists."]);
    }
    if (rows[0].project_id !== projectId) {
      throw new ValidationError([
        "A proposed cost line can only link to a feature in the same project.",
      ]);
    }
  }

  /** All lines for a project, archived included (callers filter for display). */
  /**
   * Lines for one EVALUATED design (Phase 8): manual lines with no feature
   * (common) + lines of common features (design_id NULL) + lines of that
   * design's features. The same deterministic engine consumes the result —
   * evaluating Proposed A vs Proposed B is just a different filter.
   */
  async listForDesign(projectId: string, designId: string | null): Promise<ProposedCostLine[]> {
    const clause = designId ? " OR f.design_id = ?" : "";
    const rows = await this.db.select<ProposedRow>(
      `${SELECT} WHERE project_id = ? AND (feature_id IS NULL OR EXISTS (
         SELECT 1 FROM project_features f WHERE f.id = proposed_cost_lines.feature_id AND (f.design_id IS NULL${clause})
       )) ORDER BY sort_order, created_at`,
      designId ? [projectId, designId] : [projectId],
    );
    return rows.map(mapLine);
  }

  /** Active (non-archived) proposed total for one feature — object-placement feedback. */
  async totalForFeature(featureId: string): Promise<number> {
    const rows = await this.db.select<{ total: number | null }>(
      "SELECT SUM(amount_cents) AS total FROM proposed_cost_lines WHERE feature_id = ? AND archived_at IS NULL",
      [featureId],
    );
    return Number(rows[0]?.total ?? 0);
  }

  /** All lines of one feature, archived included (object inspector / expanders). */
  async listByFeature(featureId: string): Promise<ProposedCostLine[]> {
    const rows = await this.db.select<ProposedRow>(
      `${SELECT} WHERE feature_id = ? ORDER BY sort_order, created_at`,
      [featureId],
    );
    return rows.map(mapLine);
  }

  /** Active proposed totals for every feature of a project (estimate-status badges). */
  async totalsByFeature(projectId: string): Promise<Map<string, number>> {
    const rows = await this.db.select<{ feature_id: string; total: number | null }>(
      `SELECT feature_id, SUM(amount_cents) AS total FROM proposed_cost_lines
        WHERE project_id = ? AND feature_id IS NOT NULL AND archived_at IS NULL GROUP BY feature_id`,
      [projectId],
    );
    return new Map(rows.map((r) => [r.feature_id, Number(r.total ?? 0)]));
  }

  /**
   * Phase 8.1 planning cleanup: PERMANENTLY delete a manual line with no
   * historical dependencies. Generated lines belong to their assembly, and
   * a line with linked actual expenses is protected (archive instead).
   */
  async deleteManual(id: string): Promise<void> {
    const rows = await this.db.select<{ source: string }>("SELECT source FROM proposed_cost_lines WHERE id = ?", [id]);
    if (rows.length === 0) return;
    if (rows[0].source !== "manual") {
      throw new ValidationError(["Generated lines are removed through their assembly (Edit / Remove Assembly), not deleted individually."]);
    }
    const linked = await this.db.select<{ n: number }>(
      "SELECT COUNT(*) AS n FROM actual_expenses WHERE linked_proposed_line_id = ?",
      [id],
    );
    if (Number(linked[0]?.n ?? 0) > 0) {
      throw new ValidationError(["This estimate item has actual costs linked to it and cannot be permanently deleted. Use Remove From Estimate instead."]);
    }
    await this.db.execute("DELETE FROM proposed_cost_lines WHERE id = ?", [id]);
  }

  async listByProject(projectId: string): Promise<ProposedCostLine[]> {
    const rows = await this.db.select<ProposedRow>(
      `${SELECT} WHERE project_id = ? ORDER BY sort_order, created_at`,
      [projectId],
    );
    return rows.map(mapLine);
  }

  async getLine(id: string): Promise<ProposedCostLine | null> {
    const rows = await this.db.select<ProposedRow>(`${SELECT} WHERE id = ?`, [id]);
    return rows.length > 0 ? mapLine(rows[0]) : null;
  }

  async create(projectId: string, input: ProposedCostLineInput): Promise<ProposedCostLine> {
    const problems = validateProposedCostLineInput(input);
    if (problems.length > 0) {
      throw new ValidationError(problems);
    }
    await this.assertFeatureValid(projectId, input.featureId ?? null);
    const existing = await this.listByProject(projectId);
    const maxSort = existing.reduce((m, l) => Math.max(m, l.sortOrder), 0);
    const now = new Date().toISOString();
    const line: ProposedCostLine = {
      id: generateId(),
      projectId,
      description: input.description.trim(),
      amountCents: input.amountCents,
      categoryId: input.categoryId,
      costType: input.costType,
      featureId: input.featureId ?? null,
      assemblyComponentId: null,
      calibrationRateId: null,
      source: "manual",
      sortOrder: maxSort + 10,
      notes: input.notes,
      archivedAt: null,
      createdAt: now,
      updatedAt: now,
    };
    await this.db.execute(
      `INSERT INTO proposed_cost_lines
         (id, project_id, description, amount_cents, category_id, cost_type,
          feature_id, source, sort_order, notes, created_at, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        line.id,
        line.projectId,
        line.description,
        line.amountCents,
        line.categoryId,
        line.costType,
        line.featureId,
        line.source,
        line.sortOrder,
        line.notes,
        line.createdAt,
        line.updatedAt,
      ],
    );
    return line;
  }

  async update(id: string, input: ProposedCostLineInput): Promise<void> {
    const problems = validateProposedCostLineInput(input);
    if (problems.length > 0) {
      throw new ValidationError(problems);
    }
    const existing = await this.getLine(id);
    if (!existing) {
      throw new Error(`Proposed cost line ${id} not found.`);
    }
    await this.assertFeatureValid(existing.projectId, input.featureId ?? null);
    await this.db.execute(
      `UPDATE proposed_cost_lines SET
         description = ?, amount_cents = ?, category_id = ?, cost_type = ?,
         feature_id = ?, notes = ?, updated_at = ?
       WHERE id = ?`,
      [
        input.description.trim(),
        input.amountCents,
        input.categoryId,
        input.costType,
        input.featureId ?? null,
        input.notes,
        new Date().toISOString(),
        id,
      ],
    );
  }

  /**
   * Archive (soft-delete): excluded from current Proposed Rehab Cost but the
   * stable id remains valid for linked expenses and future baselines.
   */
  async archive(id: string): Promise<void> {
    const now = new Date().toISOString();
    await this.db.execute(
      "UPDATE proposed_cost_lines SET archived_at = ?, updated_at = ? WHERE id = ? AND archived_at IS NULL",
      [now, now, id],
    );
  }

  async unarchive(id: string): Promise<void> {
    const now = new Date().toISOString();
    await this.db.execute(
      "UPDATE proposed_cost_lines SET archived_at = NULL, updated_at = ? WHERE id = ?",
      [now, id],
    );
  }
}
